import { z } from 'zod';
import { PasskeyAuthenticationResponse, PasskeyRequestOptions } from './auth.js';
/**
 * Leaving a workspace and deleting your own account (docs/adr/0040-account-deletion.md). The stores require
 * that a person can delete their account from inside the app; Google Play also wants a web page that says
 * how (`/delete-account`).
 */
/** Days between asking and the erasure; signing in again, or Cancel deletion, keeps the account. */
export const ACCOUNT_DELETION_GRACE_DAYS = 7;
/** How someone proves it is them before scheduling deletion. */
export const AccountStepUpMethod = z.enum(['passkey', 'totp', 'recovery_code', 'email_code']);
export const AccountDeletionStepUp = z.discriminatedUnion('method', [
    z.object({ method: z.literal('passkey'), challengeId: z.uuid(), response: PasskeyAuthenticationResponse }),
    z.object({ method: z.literal('totp'), code: z.string().trim().regex(/^\d{6}$/, 'Enter the six-digit code from your authenticator app') }),
    z.object({ method: z.literal('recovery_code'), code: z.string().trim().min(8).max(20) }),
    z.object({ method: z.literal('email_code'), code: z.string().trim().regex(/^\d{6}$/, 'Enter the six-digit code from the email') }),
]);
export const ScheduleAccountDeletionInput = z.object({ stepUp: AccountDeletionStepUp });
/** A workspace that stops the person deleting their account (or leaving it): they are its only owner. */
export const AccountDeletionBlocker = z.object({
    workspaceId: z.uuid(),
    workspaceName: z.string(),
    reason: z.literal('last_owner'),
});
export const AccountDeletionStatus = z.object({
    /** When the account will be erased; null when no deletion is scheduled. */
    scheduledAt: z.string().nullable(),
    graceDays: z.number().int(),
    /** Workspaces to sort out first. While a deletion is scheduled, a blocker puts it on hold. */
    blockers: z.array(AccountDeletionBlocker),
    /** The ways this person can confirm it is them, best first. */
    stepUpMethods: z.array(AccountStepUpMethod),
});
export const AccountDeletionCodeResponse = z.object({ expiresAt: z.string() });
export const AccountDeletionPasskeyOptions = z.object({ challengeId: z.uuid(), options: PasskeyRequestOptions });
/**
 * Leaving the current workspace. The workspace is the session's; `workspaceId` repeats it so a screen that
 * is out of date cannot leave the wrong one (a different id answers 404).
 */
export const LeaveWorkspaceInput = z.object({ workspaceId: z.uuid() });
/** The day the deletion completes, in words: "Friday 23 October 2026" (Sydney time, like the email). */
export function accountDeletionDate(iso, timeZone = 'Australia/Sydney') {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return '';
    return d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone });
}
/** Whole days left until `scheduledAt` (at least 0), rounding up so "in 7 days" is shown right after asking. */
export function daysUntil(scheduledAt, now = Date.now()) {
    const ms = new Date(scheduledAt).getTime() - now;
    if (Number.isNaN(ms))
        return 0;
    return Math.max(0, Math.ceil(ms / 86_400_000));
}
/** What a person's account deletion removes and what the workspace keeps, for every screen that explains it. */
export const ACCOUNT_DELETION_EXPLAINED = {
    deleted: [
        'Your name, email address and profile in every workspace (title, phone, about, status, time zone)',
        'Your sign-in methods: passkeys, authenticator app, recovery codes and every signed-in device',
        'Your notifications, reminders, My day list, Chapi conversations and personal settings',
    ],
    kept: [
        'Messages you wrote stay in their channels and direct messages, shown as "Deleted user"',
        'Files you uploaded and tasks, tickets and hours you worked on stay with the workspace',
        "Workspace owners and admins can still delete individual messages if they choose",
    ],
};
//# sourceMappingURL=account.js.map